import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import api from "../../api/api";
import { FiEye, FiEyeOff } from "react-icons/fi";

export default function EmployerSignup() {
    const navigate = useNavigate();
    const { user, setUser } = useAuth();
    const [stage, setStage] = useState(1);

    // ── Stage 1: Credentials ──────────────────────────────────────────────────

    const [creds, setCreds] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);

    // ── Stage 2: Company Info ─────────────────────────────────────────────────

    const [company, setCompany] = useState({
        companyName: "",
        jobTitle: "",
        companyWebsite: "",
        additionalNotes: "",
    });

    // Redirect already-logged-in users
    if (user) {
        navigate(
            user.isCoach ? "/coaching/dashboard"
            : user.isAdmin ? "/admin/dashboard"
            : user.isEmployer ? "/employer/dashboard"
            : "/userdashboard",
            { replace: true }
        );
        return null;
    }

    const capitalizeFirst = (str) =>
        str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

    const removeSpaces = (str) => str.replace(/\s/g, "");

    const handleCredChange = (e) => {
        const { name, value } = e.target;
        let v = removeSpaces(value);
        if (name === "firstName" || name === "lastName") v = capitalizeFirst(v);
        setCreds({ ...creds, [name]: v });
    };

    const validateStage1 = () => {
        const newErrors = {};
        const nameRegex = /^[a-zA-Z\-']+$/;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const sqlChars = /['";\-\\]/i;

        if (!creds.firstName) newErrors.firstName = "First name is required.";
        else if (!nameRegex.test(creds.firstName)) newErrors.firstName = "Names can only contain letters, hyphens, and apostrophes.";
        else if (!/^[A-Z]/.test(creds.firstName)) newErrors.firstName = "First name must start with a capital letter.";
        else if (sqlChars.test(creds.firstName)) newErrors.firstName = "First name contains unsafe characters.";

        if (!creds.lastName) newErrors.lastName = "Last name is required.";
        else if (!nameRegex.test(creds.lastName)) newErrors.lastName = "Names can only contain letters, hyphens, and apostrophes.";
        else if (!/^[A-Z]/.test(creds.lastName)) newErrors.lastName = "Last name must start with a capital letter.";
        else if (sqlChars.test(creds.lastName)) newErrors.lastName = "Last name contains unsafe characters.";

        if (!creds.email) newErrors.email = "Email address is required.";
        else if (!emailRegex.test(creds.email)) newErrors.email = "Invalid email format.";
        else if (sqlChars.test(creds.email)) newErrors.email = "Email contains unsafe characters.";

        if (!creds.password) newErrors.password = "Password is required.";
        else if (creds.password.length < 8) newErrors.password = "Password must be at least 8 characters.";
        else if (sqlChars.test(creds.password)) newErrors.password = "Password contains unsafe characters.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const submitStage1 = async (e) => {
        e.preventDefault();
        if (!validateStage1()) return;
        try {
            const res = await api.post("/auth/signup/stage1", {
                first_name: creds.firstName,
                last_name: creds.lastName,
                email: creds.email,
                password: creds.password,
            }, { withCredentials: true });
            if (res.status === 201) {
                setErrors({});
                setStage(2);
            }
        } catch (err) {
            setErrors({ server: err.response?.data?.error || "Error creating account." });
        }
    };

    // ── Stage 2: Company Info ─────────────────────────────────────────────────

    const handleCompanyChange = (e) => {
        const { name, value } = e.target;
        setCompany({ ...company, [name]: value });
    };

    const submitStage2 = async (e) => {
        e.preventDefault();
        const newErrors = {};
        if (!company.companyName.trim()) newErrors.companyName = "Company name is required.";
        if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
        setErrors({});

        try {
            const res = await api.post("/employer/apply", {
                company_name: company.companyName.trim(),
                job_title: company.jobTitle.trim() || undefined,
                company_website: company.companyWebsite.trim() || undefined,
                additional_notes: company.additionalNotes.trim() || undefined,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }, { withCredentials: true });

            if (res.status === 201) {
                // If the server finalized the session (mid-signup flow), update local auth state
                if (res.data.user) {
                    localStorage.setItem("user", JSON.stringify(res.data.user));
                    setUser(res.data.user);
                }
                setStage(3);
            }
        } catch (err) {
            setErrors({ server: err.response?.data?.error || "Failed to submit application." });
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900">
            <main className="mx-auto max-w-2xl px-4 py-20">

                {/* Progress dots */}
                <div className="flex items-center justify-center mb-12 gap-4">
                    <div className={`h-3 w-3 rounded-full ${stage >= 1 ? "bg-primary-500" : "bg-slate-300"}`} />
                    <div className={`h-1 w-20 rounded ${stage >= 2 ? "bg-primary-500" : "bg-slate-300"}`} />
                    <div className={`h-3 w-3 rounded-full ${stage >= 2 ? "bg-primary-500" : "bg-slate-300"}`} />
                    <div className={`h-1 w-20 rounded ${stage >= 3 ? "bg-primary-500" : "bg-slate-300"}`} />
                    <div className={`h-3 w-3 rounded-full ${stage >= 3 ? "bg-primary-500" : "bg-slate-300"}`} />
                </div>

                <div className="rounded-3xl border border-slate-100 bg-white/90 p-10 shadow-2xl shadow-primary-200/20 backdrop-blur-sm">

                    {/* ── Stage 1 ─────────────────────────────────────────── */}
                    {stage === 1 && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <h2 className="text-3xl font-extrabold mb-2">Employer Sign Up</h2>
                            <p className="text-slate-500 mb-8">Create your account to get started.</p>

                            <form onSubmit={submitStage1} className="space-y-5">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-semibold mb-1 block">First Name</label>
                                        <input
                                            name="firstName"
                                            value={creds.firstName}
                                            onChange={handleCredChange}
                                            className={`w-full rounded-xl border p-3 bg-transparent transition-colors ${errors.firstName ? "border-red-500" : "border-slate-300"}`}
                                        />
                                        {errors.firstName && <p className="text-red-500 text-xs mt-1 font-medium">{errors.firstName}</p>}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold mb-1 block">Last Name</label>
                                        <input
                                            name="lastName"
                                            value={creds.lastName}
                                            onChange={handleCredChange}
                                            className={`w-full rounded-xl border p-3 bg-transparent transition-colors ${errors.lastName ? "border-red-500" : "border-slate-300"}`}
                                        />
                                        {errors.lastName && <p className="text-red-500 text-xs mt-1 font-medium">{errors.lastName}</p>}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-semibold mb-1 block">Email Address</label>
                                    <input
                                        name="email"
                                        type="email"
                                        value={creds.email}
                                        onChange={handleCredChange}
                                        className={`w-full rounded-xl border p-3 bg-transparent transition-colors ${errors.email ? "border-red-500" : "border-slate-300"}`}
                                    />
                                    {errors.email && <p className="text-red-500 text-xs mt-1 font-medium">{errors.email}</p>}
                                </div>

                                <div>
                                    <label className="text-sm font-semibold mb-1 block">Password</label>
                                    <div className="relative">
                                        <input
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            value={creds.password}
                                            onChange={handleCredChange}
                                            className={`w-full rounded-xl border p-3 pr-11 bg-transparent transition-colors ${errors.password ? "border-red-500" : "border-slate-300"}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                            tabIndex={-1}
                                        >
                                            {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                        </button>
                                    </div>
                                    {errors.password && <p className="text-red-500 text-xs mt-1 font-medium">{errors.password}</p>}
                                    {creds.password && (
                                        <p className={`text-[10px] mt-1 font-bold ${creds.password.length >= 8 ? "text-primary-500" : "text-slate-400"}`}>
                                            {creds.password.length >= 8 ? "✓ Length Requirement Met" : "Requires 8+ characters"}
                                        </p>
                                    )}
                                </div>

                                <button type="submit" className="w-full bg-primary-600 text-white py-4 rounded-xl font-bold hover:bg-primary-700 transition-all shadow-lg shadow-primary-900/20 active:scale-[0.98]">
                                    Next Step: Company Info →
                                </button>
                            </form>

                            <p className="text-center text-sm mt-6 text-slate-500">
                                Already have an account?{" "}
                                <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">Sign in</Link>
                            </p>
                        </div>
                    )}

                    {/* ── Stage 2 ─────────────────────────────────────────── */}
                    {stage === 2 && (
                        <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                            <h2 className="text-3xl font-extrabold mb-2">Company Details</h2>
                            <p className="text-slate-500 mb-8">Tell us about the company you represent. Our team will review your application.</p>

                            <form onSubmit={submitStage2} className="space-y-5">
                                <div>
                                    <label className="text-sm font-semibold mb-1 block">Company Name <span className="text-red-500">*</span></label>
                                    <input
                                        name="companyName"
                                        value={company.companyName}
                                        onChange={handleCompanyChange}
                                        placeholder="e.g. Acme Energy Corp"
                                        className={`w-full rounded-xl border p-3 bg-transparent transition-colors ${errors.companyName ? "border-red-500" : "border-slate-300"}`}
                                    />
                                    {errors.companyName && <p className="text-red-500 text-xs mt-1 font-medium">{errors.companyName}</p>}
                                </div>

                                <div>
                                    <label className="text-sm font-semibold mb-1 block">Your Job Title <span className="text-slate-400 font-normal">(optional)</span></label>
                                    <input
                                        name="jobTitle"
                                        value={company.jobTitle}
                                        onChange={handleCompanyChange}
                                        placeholder="e.g. Talent Acquisition Manager"
                                        className="w-full rounded-xl border border-slate-300 p-3 bg-transparent transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="text-sm font-semibold mb-1 block">Company Website <span className="text-slate-400 font-normal">(optional)</span></label>
                                    <input
                                        name="companyWebsite"
                                        value={company.companyWebsite}
                                        onChange={handleCompanyChange}
                                        placeholder="https://yourcompany.com"
                                        className="w-full rounded-xl border border-slate-300 p-3 bg-transparent transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="text-sm font-semibold mb-1 block">Why are you applying? <span className="text-slate-400 font-normal">(optional)</span></label>
                                    <textarea
                                        name="additionalNotes"
                                        value={company.additionalNotes}
                                        onChange={handleCompanyChange}
                                        rows={4}
                                        placeholder="Tell us about what you're looking for and how you plan to use the platform..."
                                        className="w-full rounded-xl border border-slate-300 p-3 bg-transparent transition-colors resize-none"
                                    />
                                </div>

                                <button type="submit" className="w-full bg-primary-600 text-white py-4 rounded-xl font-bold hover:bg-primary-700 transition-all shadow-lg active:scale-[0.98]">
                                    Submit Application
                                </button>
                            </form>
                        </div>
                    )}

                    {/* ── Stage 3: Confirmation ────────────────────────────── */}
                    {stage === 3 && (
                        <div className="animate-in fade-in slide-in-from-right-4 duration-500 text-center">
                            <div className="flex justify-center mb-6">
                                <div className="h-20 w-20 rounded-full bg-primary-100 flex items-center justify-center">
                                    <svg className="h-10 w-10 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            </div>

                            <h2 className="text-3xl font-extrabold mb-3">Application Submitted!</h2>
                            <p className="text-slate-500 mb-2">
                                Thank you for applying. Our team will review your application and reach out to you by email.
                            </p>
                            <p className="text-slate-400 text-sm mb-8">
                                In the meantime, you can browse the platform as a regular member.
                            </p>

                            <button
                                onClick={() => navigate("/userdashboard", { replace: true })}
                                className="w-full bg-primary-600 text-white py-4 rounded-xl font-bold hover:bg-primary-700 transition-all shadow-lg active:scale-[0.98]"
                            >
                                Go to Dashboard →
                            </button>
                        </div>
                    )}

                    {errors.server && (
                        <p className="mt-4 text-red-500 text-sm text-center font-medium">⚠️ {errors.server}</p>
                    )}
                </div>
            </main>
        </div>
    );
}
